import { HttpErrorResponse } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, throwError } from 'rxjs';
import { first, switchMap, catchError, finalize, filter, take } from 'rxjs/operators';
import atob from 'atob';

class JwtServiceInterface {
}

const JWT_SERVICE_TOKEN = new InjectionToken('JWT_SERVICE_TOKEN');
const HEADER_SKIP_REFRESH_TOKEN = 'X-Skip-Refresh-Token';

class JwtInterceptor {
    constructor(jwtService) {
        this.jwtService = jwtService;
        this.isRefreshingToken = false;
        this.tokenSubject = new BehaviorSubject(null);
        this.jwtHelperService = new JwtHelperService();
    }
    addToken(request, token) {
        const hasXAuthTypeJwt = request.headers.get('x-auth-type') === 'jwt';
        const otherheaders = hasXAuthTypeJwt
            ? {
                'x-auth-token': token,
                'x-auth-type': 'jwt',
            }
            : {};
        return request.clone({
            setHeaders: { Authorization: `Bearer ${token}`, ...otherheaders },
        });
    }
    intercept(request, next) {
        return this.jwtService.getTokens().pipe(first(), switchMap((tokens) => {
            if (tokens &&
                !this.jwtService.blacklistRoutes.some((apiUrl) => {
                    return apiUrl instanceof RegExp ? apiUrl.test(request.url) : request.url.indexOf(apiUrl) !== -1;
                })) {
                if (this.jwtHelperService.isTokenExpired(tokens.token) && !request.headers.has(HEADER_SKIP_REFRESH_TOKEN)) {
                    return this.handle401Error(request, next, tokens);
                }
                return next.handle(this.addToken(request, tokens.token)).pipe(catchError((error) => {
                    if (error instanceof HttpErrorResponse && error.status === 401) {
                        return this.handle401Error(request, next, tokens);
                    }
                    return throwError(error);
                }));
            }
            return next.handle(request);
        }));
    }
    handle401Error(request, next, tokensLocal) {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next(null);
            return this.jwtService.refreshToken(tokensLocal).pipe(switchMap((newTokens) => {
                if (newTokens) {
                    this.tokenSubject.next(newTokens);
                    this.jwtService.saveLocalTokens({
                        token: newTokens.token,
                        refreshToken: newTokens.refreshToken,
                    });
                    return next.handle(this.addToken(request, newTokens.token));
                }
                // If we don't get a new token, we are in trouble so logout.
                return this.logoutUser();
            }), catchError((error) => {
                const status = error.status;
                // If there is an exception calling 'refreshToken', bad news so logout.
                if (status >= 400 && status < 500) {
                    return this.logoutUser(error);
                }
                return throwError(error);
            }), finalize(() => {
                this.isRefreshingToken = false;
            }));
        }
        return this.tokenSubject.pipe(filter((tokens) => tokens !== null), take(1), switchMap((tokens) => {
            return next.handle(this.addToken(request, tokens.token));
        }));
    }
    logoutUser(error) {
        this.jwtService.logout();
        return throwError(error ? error : 'LOGOUT');
    }
}
JwtInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: JwtInterceptor, deps: [{ token: JWT_SERVICE_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable });
JwtInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: JwtInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: JwtInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: JwtServiceInterface, decorators: [{
                    type: Inject,
                    args: [JWT_SERVICE_TOKEN]
                }] }]; } });

function deserializeJwt(jwt) {
    const payloadUrl = jwt.split('.')[1];
    if (!payloadUrl) {
        return null;
    }
    const payload = payloadUrl.replace('-', '+').replace('_', '/');
    return payload ? JSON.parse(atob(payload)) : null;
}

/**
 * Generated bundle index. Do not edit.
 */

export { HEADER_SKIP_REFRESH_TOKEN, JWT_SERVICE_TOKEN, JwtInterceptor, JwtServiceInterface, deserializeJwt };
